import { queryOptions, useQueries } from '@tanstack/react-query';
import { useEffect } from 'react';

import { queryClient } from '@/api/queryClient';
import { isMediaPending, pollWhileMediaPending } from '@/common/media';
import { PERMISSIONS } from '@/permissions/permissions.catalog';
import { usePermissions } from '@/permissions/usePermissions';
import { getBidRooms, getRoomMessages } from '@/store/chats/chat.api';
import { useChats } from '@/store/chats/chats.store';
import { getInvoicesByTask } from '@/store/expenses-store/invoices/invoices.api';
import { getQuotesByTask } from '@/store/expenses-store/quotes/quotes.api';
import { useMessages } from '@/store/messages/messages.store';

import { fetchTaskById } from './tasks.api';
import type { TaskResponse } from './tasks.dto';
import { taskKeys } from './tasks.queries';
import { useTasks } from './tasks.store';

/**
 * What the task card (`SingleTaskModal`) reads: the task, its invoices, its quotes, its
 * service requests and its comments — cached per task, so a card opened again opens
 * from cache.
 *
 * The zustand stores stay the source the rest of the app reads — `currentTask` above
 * all, which the calendar, the dashboards and the edit sheets follow. The query
 * functions here are plain requests that touch no store, and the stores are written
 * from the results in one place, when a card shows them (`useTaskCard`).
 *
 * There is no prefetch on row hover on purpose: it sent five requests for every row the
 * pointer crossed, and again every time they went stale, to save one wait on a first open.
 */

/**
 * A card closed and opened again within this is not asked for again. Older than this,
 * it still opens from cache and refreshes behind itself.
 */
const TASK_CARD_STALE_TIME = 15_000;

/**
 * Invoices, quotes, requests and comments are no part of the task, so they sit outside
 * `taskKeys.all`: re-statusing a task should refetch the task, not four other things.
 */
export const taskCardKeys = {
  all: ['task-card'] as const,
  card: (id: string) => [...taskCardKeys.all, id] as const,
  invoices: (id: string) => [...taskCardKeys.card(id), 'invoices'] as const,
  quotes: (id: string) => [...taskCardKeys.card(id), 'quotes'] as const,
  bids: (id: string) => [...taskCardKeys.card(id), 'bids'] as const,
  comments: (id: string) => [...taskCardKeys.card(id), 'comments'] as const,
};

/**
 * The card waits for every one of these before it opens, so a retry is a delay the
 * person sees — with the client's default three, one failing section held the card shut
 * for seconds. A section that fails is left out instead, and stays a log rather than a
 * toast over a task someone just opened.
 */
const reported =
  <T>(what: string, load: () => Promise<T>) =>
  async () => {
    try {
      return await load();
    } catch (error) {
      console.error(`Failed to load ${what}:`, error);
      throw error;
    }
  };

const shared = { staleTime: TASK_CARD_STALE_TIME, retry: false } as const;

const taskCardQueries = (id: string) => ({
  task: queryOptions({
    ...shared,
    queryKey: taskKeys.detail(id),
    queryFn: reported('the task', () => fetchTaskById(id)),
    // Activity tells nobody when a video among the task's files has been processed.
    refetchInterval: pollWhileMediaPending((task: TaskResponse) =>
      (task.documents ?? []).some((document) => isMediaPending(document.media_status)),
    ),
  }),
  invoices: queryOptions({
    ...shared,
    queryKey: taskCardKeys.invoices(id),
    queryFn: reported('the task invoices', () => getInvoicesByTask(id)),
  }),
  quotes: queryOptions({
    ...shared,
    queryKey: taskCardKeys.quotes(id),
    queryFn: reported('the task quotes', () => getQuotesByTask(id)),
  }),
  bids: queryOptions({
    ...shared,
    queryKey: taskCardKeys.bids(id),
    queryFn: reported('the task requests', () => getBidRooms(id)),
  }),
  comments: queryOptions({
    ...shared,
    queryKey: taskCardKeys.comments(id),
    queryFn: reported('the task comments', () => getRoomMessages(id).then((page) => page.items)),
  }),
});

/**
 * The store's copy of the open task moves on its own — a status tick, an attached
 * file, a participant — and the cache has to follow it, or the next open of that task
 * would show the version from before the change.
 */
useTasks.subscribe((state, previous) => {
  const task = state.currentTask;
  if (task && task !== previous.currentTask) {
    queryClient.setQueryData(taskKeys.detail(task.id), task);
  }
});

/**
 * The same for a task's comments: a comment sent, deleted or read, or one arriving over
 * the socket, lands in `messagesByRoom`, and a cached card has to carry it. Only threads
 * a card has asked for — every other chat room stays out of the cache.
 */
useMessages.subscribe((state, previous) => {
  if (state.messagesByRoom === previous.messagesByRoom) return;
  for (const [roomId, thread] of Object.entries(state.messagesByRoom)) {
    if (thread === previous.messagesByRoom[roomId]) continue;
    const key = taskCardKeys.comments(roomId);
    if (queryClient.getQueryData(key) !== undefined) {
      queryClient.setQueryData(key, thread);
    }
  }
});

const useTaskCardPermissions = () => {
  const { can } = usePermissions();
  return {
    canViewInvoices: can(PERMISSIONS.wallet.Invoices),
    canViewQuotes: can(PERMISSIONS.wallet.Quotes),
  };
};

export const useTaskCard = (id: string) => {
  const { canViewInvoices, canViewQuotes } = useTaskCardPermissions();
  const queries = taskCardQueries(id);

  const [task, invoices, quotes, bids, comments] = useQueries({
    queries: [
      queries.task,
      { ...queries.invoices, enabled: canViewInvoices },
      { ...queries.quotes, enabled: canViewQuotes },
      queries.bids,
      queries.comments,
    ],
  });

  // Query results into the stores, for everything else that reads them.

  useEffect(() => {
    if (task.data && useTasks.getState().currentTask !== task.data) {
      useTasks.setState({ currentTask: task.data });
    }
  }, [task.data]);

  useEffect(() => {
    if (bids.data) useChats.setState({ bids: bids.data });
  }, [bids.data]);

  // The card's comments section does not fetch the thread itself — this is its one load.
  // The cache never runs behind the store (see the subscription above), so its copy can
  // always go in, and a refresh of a stale card reaches the section through here.
  useEffect(() => {
    const thread = comments.data;
    if (!thread || useMessages.getState().messagesByRoom[id] === thread) return;
    useMessages.setState((state) => ({
      messagesByRoom: { ...state.messagesByRoom, [id]: thread },
    }));
  }, [comments.data, id]);

  // A disabled query never leaves `pending`, so a section the person cannot see counts
  // as settled rather than holding the card shut.
  const settled = (query: { isPending: boolean }, enabled = true) => !enabled || !query.isPending;

  return {
    task: task.data ?? null,
    // Guarded here as well as at the fetch, so losing the permission mid-session empties
    // the section rather than leaving the last rows it managed to read.
    invoices: canViewInvoices ? (invoices.data ?? null) : null,
    quotes: canViewQuotes ? (quotes.data ?? null) : null,
    bids: bids.data ?? null,
    ready:
      settled(task) &&
      settled(invoices, canViewInvoices) &&
      settled(quotes, canViewQuotes) &&
      settled(bids) &&
      settled(comments),
  };
};
